<template>
  <div
    v-if="show && comp"
    class="comp-popup-overlay"
    @click.self="$emit('close')"
  >
    <div class="comp-popup-content">
      <!-- Header -->
      <div class="comp-popup-header">
        <div>
          <h3 class="comp-popup-title">{{ comp.address_line_1 }}</h3>
          <p class="comp-popup-subtitle">Comparable Property</p>
        </div>
        <button @click="$emit('close')" class="comp-popup-close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <!-- Body -->
      <div class="comp-popup-body">
        <!-- Comp Image -->
        <div v-if="compImage" class="comp-image-container">
          <!-- Loading Spinner -->
          <div v-if="imageLoading" class="image-loader">
            <div class="spinner"></div>
          </div>
          <img 
            :src="compImage" 
            :alt="comp.address_line_1" 
            class="comp-image"
            @load="onImageLoad"
            @error="onImageError"
          />
        </div>
         
        <!-- Comp Metrics Table -->
        <div class="comp-stats-table">
          <table>
            <tbody>
              <tr v-if="comp.total_square_footage">
                <th>Comp Size</th>
                <td>{{ formatNumber(comp.total_square_footage) }} sq ft</td>
              </tr>
              <tr v-if="comp.pivot && comp.pivot.ask_price">
                <th>Ask Price PSF</th>
                <td>{{ formatCurrency(comp.pivot.ask_price) }}</td>
              </tr>
              <tr v-if="comp.pivot && comp.pivot.score !== null && comp.pivot.score !== undefined">
                <th>Score</th>
                <td>
                  <span :class="getScoreClass(comp.pivot.score)">
                    {{ comp.pivot.score }}
                  </span>
                </td>
              </tr>
              <tr v-if="comp.building_type">
                <th>Building Type</th>
                <td>{{ comp.building_type }}</td>
              </tr>
              <tr v-if="comp.year_built">
                <th>Year Built</th>
                <td>{{ comp.year_built }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="comp-popup-footer">
        <a :href="compViewUrl" class="comp-popup-link-button">
          View Full Details
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CompPopup',
  
  props: {
    comp: {
      type: Object,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      imageLoading: true
    };
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset loading state when popup opens
        this.imageLoading = true;
      }
    }
  },
  
  computed: {
    compImage() {
      // Check if comp has an image
      if (this.comp?.image) {
        // If image is an object with view_url property
        if (typeof this.comp.image === 'object' && this.comp.image.view_url) {
          return this.comp.image.view_url;
        }
        // If image is a string URL
        if (typeof this.comp.image === 'string') {
          return this.comp.image;
        }
      }
      return null;
    },
    
    compLocation() {
      const parts = [];
      
      if (this.comp?.city) parts.push(this.comp.city);
      if (this.comp?.state) parts.push(this.comp.state);
      if (this.comp?.postal_code) parts.push(this.comp.postal_code);
      
      return parts.join(', ');
    },
    
    compViewUrl() {
      // Construct URL to view comp details
      // Assuming comps are accessed via /projects/{projectId}/comps/{compId}
      if (this.comp?.id && this.comp?.pivot?.project_id) {
        return `/projects/${this.comp.pivot.project_id}/comps/${this.comp.id}`;
      }
      return '#';
    }
  },
  
  mounted() {
    // Add ESC key listener
    document.addEventListener('keydown', this.handleEscKey);
  },
  
  beforeDestroy() {
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleEscKey);
  },
  
  methods: {
    handleEscKey(event) {
      if (event.key === 'Escape' && this.show) {
        this.$emit('close');
      }
    },
    
    onImageLoad() {
      this.imageLoading = false;
    },
    
    onImageError() {
      this.imageLoading = false;
    },
    
    formatNumber(value) {
      if (!value) return '0';
      return new Intl.NumberFormat('en-US').format(value);
    },
    
    formatCurrency(value) {
      if (!value) return 'N/A';
      // If value is already formatted as currency, return it
      if (typeof value === 'string' && value.includes('$')) {
        return value;
      }
      // Otherwise format as currency
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    
    getScoreClass(score) {
      // Return CSS class based on score value
      if (score >= 80) return 'score-high';
      if (score >= 60) return 'score-medium';
      return 'score-low';
    }
  }
};
</script>
